/**
 * <footer-reveal> — sections/brand-footer.liquid.
 * "Sticky reveal" for the bottom strip (star, copyright, seeds), like footer.design: the strip
 * is fixed to the bottom of the viewport and only shows through this element's box
 * (`clip-path: inset(0)` in CSS), so as the page scrolls to its end the content above slides
 * away and uncovers it. This element only keeps its own height equal to the strip's height,
 * because a fixed child takes no space in the flow. Reduced motion → plain static strip.
 */
class FooterReveal extends HTMLElement {
  /** @type {ResizeObserver | null} */
  #observer = null;

  connectedCallback() {
    const strip = this.firstElementChild;
    if (!strip) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.classList.add('is-static');
      return;
    }

    this.#observer = new ResizeObserver(() => {
      this.style.height = `${strip.getBoundingClientRect().height}px`;
    });
    this.#observer.observe(strip);
    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('footer-reveal')) {
  customElements.define('footer-reveal', FooterReveal);
}

/**
 * <footer-groups> — the footer's menu and contact columns.
 * They are `<details>` so the mobile design's accordion works without JavaScript, and they are
 * rendered open so the desktop design — three columns with no toggle — is right without it too.
 * CSS cannot reopen a closed `<details>`, so the breakpoint drives `open` from here: collapse
 * below 750px, open from 750px up, and refuse a toggle on desktop (a keyboard user can still
 * reach the summary even though CSS takes it out of the pointer's way).
 * A toggle the visitor makes on mobile is left alone until the breakpoint changes.
 */
class FooterGroups extends HTMLElement {
  /** @type {MediaQueryList | null} */
  #desktop = null;
  #onBreakpoint = () => this.#sync();
  #onToggle = (/** @type {Event} */ event) => {
    const group = event.target;
    if (this.#desktop?.matches && group instanceof HTMLDetailsElement) group.open = true;
  };

  connectedCallback() {
    this.#desktop = window.matchMedia('(min-width: 750px)');
    this.#desktop.addEventListener('change', this.#onBreakpoint);
    // `toggle` does not bubble, so listen in the capture phase
    this.addEventListener('toggle', this.#onToggle, true);
    this.#sync();
  }

  disconnectedCallback() {
    this.#desktop?.removeEventListener('change', this.#onBreakpoint);
    this.removeEventListener('toggle', this.#onToggle, true);
    this.#desktop = null;
  }

  #sync() {
    const open = this.#desktop?.matches ?? true;
    for (const group of this.querySelectorAll('details')) group.open = open;
  }
}

if (!customElements.get('footer-groups')) {
  customElements.define('footer-groups', FooterGroups);
}
